


import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { createSession, uploadDocuments } from "../api";
import UploadArea from "./UploadArea";

const THINKING_STATES = [
  "Reading files...",
  "Analyzing content...",
  "Processing documents...",
  "Extracting information..."
];

export default function FileUploadModal({ isOpen, onClose, agentId, agentName }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sessionData, setSessionData] = useState(null);
  const [sessionName, setSessionName] = useState("");
  const [newSessionId, setNewSessionId] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [voiceGender, setVoiceGender] = useState("female");
  const [includeAudio, setIncludeAudio] = useState(true);
  const [currentThinkingState, setCurrentThinkingState] = useState("");
  const [thinkingIndex, setThinkingIndex] = useState(0);

  // Dropdown options
  const voiceGenderOptions = [
    { label: 'Female', value: 'female' },
    { label: 'Male', value: 'male' }
  ];

  const audioOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  // Rotate through thinking states
  useEffect(() => {
    if (!uploading) return;

    const interval = setInterval(() => {
      setThinkingIndex((prev) => {
        const next = (prev + 1) % THINKING_STATES.length;
        setCurrentThinkingState(THINKING_STATES[next]);
        return next;
      });
    }, 12000);

    return () => clearInterval(interval);
  }, [uploading]);

  useEffect(() => {
    if (isOpen && agentId && agentName) {
      // Reset state when modal opens
      setFiles([]);
      setSessionName(agentName);
      setNewSessionId(null);
      setCreatingSession(true);
      setUploading(false);
      setSessionData(null);
      setCurrentThinkingState("");
      setThinkingIndex(0);
      
      // Create session immediately when modal opens
      initializeSession();
    }
  }, [isOpen, agentId, agentName]);

  const initializeSession = async () => {
    try {
      // Create session with agent_id and agent name
      const payload = {
        agent_id: agentId,
        session_name: agentName
      };
      
      console.log("Creating session with payload:", payload);
      const sessionResponse = await createSession(payload);
      console.log("Session created:", sessionResponse.data);
      
      // Store the new session ID from response
      if (sessionResponse.data?.session_id) {
        setNewSessionId(sessionResponse.data.session_id);
      }
      
      // Set session data for display
      setSessionData({
        agent_id: agentId,
        session_name: payload.session_name
      });
      setSessionName(payload.session_name);
      
    } catch (error) {
      console.error("Error initializing session:", error);
      alert("Error creating session. Please try again.");
    } finally {
      setCreatingSession(false);
    }
  };

  const handleFilesAdded = (newFiles) => {
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (!newSessionId) {
      alert("Session ID not available. Please try again.");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setThinkingIndex(0);
    setCurrentThinkingState(THINKING_STATES[0]);
    
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 15;
        });
      }, 300);
      
      // Upload files using the uploadDocuments API
      console.log("Uploading files to session:", newSessionId);
      console.log("Files:", files);
      console.log("Voice Gender:", voiceGender);
      console.log("Include Audio:", includeAudio);
      
      const response = await uploadDocuments(newSessionId, files, voiceGender, includeAudio);
      console.log("Upload response:", response.data);
      
      // Complete the progress
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Small delay to show 100% before navigation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate to conversation page with upload response data
      navigate(`/conversation/${newSessionId}`, {
        state: {
          uploadResponse: response.data,
          agentId: agentId,
          agentName: agentName
        }
      });
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Error uploading files. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentThinkingState("");
    }
  };

  const handleClearFiles = () => {
    setFiles([]);
  };

  const headerTemplate = (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 m-0">Upload Files</h2>
        <p className="text-gray-600 mt-1 mb-0">Upload documents for {agentName}</p>
      </div>
    </div>
  );

  const footerTemplate = (
    <div className="flex gap-4">
      <Button
        label="Cancel"
        onClick={onClose}
        className="flex-1 p-button-outlined"
        disabled={uploading || creatingSession}
      />
      {files.length > 0 && (
        <Button
          label="Clear All"
          onClick={handleClearFiles}
          className="flex-1 p-button-outlined"
          disabled={uploading || creatingSession}
        />
      )}
      <Button
        label={creatingSession ? "Creating Session..." : uploading ? "Uploading..." : "Upload Files"}
        onClick={handleUpload}
        className="flex-1"
        disabled={uploading || creatingSession || files.length === 0}
        loading={creatingSession || uploading}
      />
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes progressStripesMove {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
        .progress-striped-anim {
          position: absolute;
          inset: 0;
          border-radius: 8px;
          pointer-events: none;
          background-image: linear-gradient(45deg,
            rgba(255,255,255,0.18) 25%,
            rgba(255,255,255,0.08) 25%,
            rgba(255,255,255,0.08) 50%,
            rgba(255,255,255,0.18) 50%,
            rgba(255,255,255,0.18) 75%,
            rgba(255,255,255,0.08) 75%,
            rgba(255,255,255,0.08)
          );
          background-size: 40px 40px;
          animation: progressStripesMove 1s linear infinite;
          mix-blend-mode: overlay;
        }

        @keyframes dotBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .dot {
          display: inline-block;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: #4F39F6;
          animation: dotBounce 1.4s infinite;
        }
        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
      <Dialog
        header={headerTemplate}
        visible={isOpen}
        onHide={onClose}
        style={{ width: '80vw', maxWidth: '800px' }}
        modal
        draggable={false}
        resizable={false}
        footer={footerTemplate}
        className="p-fluid"
      >
        <div className="space-y-6">
          {/* Upload Area */}
          <div>
            <UploadArea onFilesAdded={handleFilesAdded} />
          </div>

          {/* Upload Progress Bar with Thinking State */}
          {uploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">
                    {currentThinkingState}
                  </span>
                  <div className="flex gap-1">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-[#4F39F6]">
                  {Math.round(uploadProgress)}%
                </span>
              </div>
              <div className="relative overflow-hidden rounded-md">
                <ProgressBar 
                  value={uploadProgress} 
                  showValue={false}
                  className="h-3"
                  style={{ height: '12px' }}
                />
                <div className="progress-striped-anim" />
              </div>
            </div>
          )}

          {/* File Queue */}
          {files.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Files to Upload ({files.length})
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {files.map((file, i) => (
                  <div
                    key={`${file.name}-${file.size}-${i}`}
                    className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-[#4F39F6] mr-3 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-sm p-button-danger"
                      onClick={() => handleRemoveFile(i)}
                      tooltip="Remove file"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}